/**
 * The sign-in page.
 *
 * One page, several steps, because Cognito decides which step comes next — password,
 * then possibly a new password, then possibly an MFA code or MFA enrolment. Rendering
 * them as one component with a `step` discriminant means the back button, the error
 * area and the layout are written once.
 *
 * There is no link to register. Accounts are created by the office; a "sign up" link
 * on an employee-monitoring system would be a door with no room behind it.
 */

import { useEffect, useState, type FormEvent } from 'react';

import { Logo } from '@/components/Mark';
import { PreferenceToggles } from '@/components/Preferences';
import { useI18n } from '@/i18n';

import { useAuth } from './AuthProvider';
import { authErrorKey } from './errors';
import { ForgotPassword } from './ForgotPassword';

/** Shared field styling. Tokens, not hex — the page has to work in both themes. */
const FIELD =
  'w-full h-11 px-3 rounded-lg bg-surface border border-border text-text text-sm ' +
  'outline-none transition-colors placeholder:text-subtle ' +
  'focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/25';

const BUTTON =
  'w-full h-11 rounded-lg bg-primary text-white text-sm font-semibold transition-opacity ' +
  'hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed';

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M1.8 10S4.7 4.8 10 4.8 18.2 10 18.2 10 15.3 15.2 10 15.2 1.8 10 1.8 10Z" />
      <circle cx="10" cy="10" r="2.4" />
      {off ? <path d="M3.5 3.5l13 13" /> : null}
    </svg>
  );
}

/*
 * "Remember me": the email only, on this device, so a passkey sign-in is one click.
 * Cognito finds a passkey by the account it belongs to, which is why the email was
 * always needed first. Never the password — the browser's own password manager is the
 * place for that. Storage can be blocked (private mode); everything works without it.
 */
const REMEMBER_KEY = 'hh.rememberedEmail';

function readRemembered(): string | null {
  try {
    return localStorage.getItem(REMEMBER_KEY);
  } catch {
    return null;
  }
}

function writeRemembered(email: string | null) {
  try {
    if (email) localStorage.setItem(REMEMBER_KEY, email);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    /* storage blocked: nothing to remember with, and nothing breaks */
  }
}

export function SignInPage() {
  const { t } = useI18n();
  const {
    step,
    signIn,
    signInWithPasskey,
    submitChallenge,
    chooseAuthenticatorApp,
    addPasskeyAndContinue,
    skipSecondFactor,
    resetStep,
    endReason,
    configured,
  } = useAuth();

  /*
   * Only offered where it can work. A browser without WebAuthn would show a button that
   * fails every time, which is worse than no button.
   */
  const passkeysSupported = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

  const [remembered, setRemembered] = useState<string | null>(readRemembered);
  const [remember, setRemember] = useState(remembered !== null);
  const [email, setEmail] = useState(remembered ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgot, setForgot] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /* Why the last session ended, shown once. Not an error — nothing went wrong. */
  useEffect(() => {
    if (endReason === 'idle') setNotice(t('auth.session.timedOut'));
    else if (endReason === 'absolute') setNotice(t('auth.session.maxReached'));
    else if (endReason === 'noAccess') setNotice(t('auth.session.noPortalAccess'));
  }, [endReason, t]);

  /* Moving between steps must not carry the previous step's error with it. */
  useEffect(() => {
    setError(null);
    setCode('');
  }, [step.kind]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(t(authErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  /** Keep or forget the address — only once it has actually signed someone in. */
  const keep = (address: string) => writeRemembered(remember ? address : null);

  const onCredentials = (e: FormEvent) => {
    e.preventDefault();
    setNotice(null);
    const address = email.trim();
    void run(async () => {
      await signIn(address, password);
      keep(address);
    });
  };

  const passkeySignIn = (address: string) => {
    setNotice(null);
    void run(async () => {
      await signInWithPasskey(address);
      keep(address);
    });
  };

  const forget = () => {
    writeRemembered(null);
    setRemembered(null);
    setRemember(false);
    setEmail('');
  };

  const onChallenge = (e: FormEvent) => {
    e.preventDefault();
    void run(() => submitChallenge(code.trim()));
  };

  const onNewPassword = (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('auth.newPassword.mismatch'));
      return;
    }
    void run(() => submitChallenge(newPassword));
  };

  if (forgot) {
    return (
      <Shell>
        <ForgotPassword
          initialEmail={email}
          onDone={(message) => {
            setForgot(false);
            setNotice(message);
            setPassword('');
          }}
          onCancel={() => setForgot(false)}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-xl font-bold text-center">
        {step.kind === 'credentials'
          ? t('auth.title')
          : step.kind === 'mfa'
            ? t('auth.mfa.title')
            : step.kind === 'setupMfa'
              ? t('auth.setupMfa.title')
              : step.kind === 'secure'
                ? t('auth.secure.title')
                : t('auth.newPassword.title')}
      </h1>

      {/*
        No subtitle on the credentials step. "HH Electrical internal portal" under a
        mark that already says HH Electrical, above a form that plainly is a sign-in,
        is a line nobody reads. The later steps keep theirs because they carry an
        instruction — where the code came from, what happens to the temporary password.
      */}
      {step.kind === 'credentials' ? null : (
        <p className="mt-1.5 text-center text-sm text-muted">
          {step.kind === 'mfa'
            ? t(step.via === 'email' ? 'auth.mfa.subtitleEmail' : 'auth.mfa.subtitle')
            : step.kind === 'setupMfa'
              ? t('auth.setupMfa.subtitle')
              : step.kind === 'secure'
                ? t('auth.secure.subtitle')
                : t('auth.newPassword.subtitle')}
        </p>
      )}

      {notice ? (
        <p
          className="mt-4 rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-muted"
          role="status"
        >
          {notice}
        </p>
      ) : null}

      {!configured ? (
        <p
          className="mt-4 rounded-lg border border-[var(--color-danger)] bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {t('auth.error.notConfigured')}
        </p>
      ) : null}

      {error ? (
        <p
          className="mt-4 rounded-lg border border-[var(--color-danger)] bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {/*
        Welcome back: the remembered account, and its passkey one click away. The
        password form stays underneath for when the passkey is on another device.
      */}
      {step.kind === 'credentials' && remembered && passkeysSupported ? (
        <div className="mt-5 space-y-2.5">
          <div className="rounded-lg border border-border bg-surface-alt px-3 py-2.5 text-center">
            <div className="text-xs text-muted">{t('auth.remember.welcomeBack')}</div>
            <div className="truncate text-sm font-semibold">{remembered}</div>
          </div>
          <button
            type="button"
            disabled={busy || !configured}
            onClick={() => passkeySignIn(remembered)}
            className={BUTTON}
          >
            <span className="inline-flex items-center justify-center gap-2">
              <PasskeyIcon />
              {busy ? t('auth.signingIn') : t('auth.passkey.signIn')}
            </span>
          </button>
          <button
            type="button"
            onClick={forget}
            className="w-full text-center text-xs text-muted hover:text-primary transition-colors"
          >
            {t('auth.remember.notYou')}
          </button>
          <div className="flex items-center gap-3 pt-1" aria-hidden="true">
            <span className="h-px flex-1 bg-border" />
            <span className="text-[11px] uppercase tracking-wider text-subtle">{t('auth.remember.orPassword')}</span>
            <span className="h-px flex-1 bg-border" />
          </div>
        </div>
      ) : null}

      {step.kind === 'credentials' ? (
        <form onSubmit={onCredentials} className="mt-5 space-y-3.5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">{t('auth.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus={!remembered}
              required
              className={FIELD}
              placeholder="nome@hhelectrical.it"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">
              {t('auth.password')}
            </span>
            {/* ltr: a password is Latin text, and the eye sits on the same side as the
                input's own end padding only if both read the same direction. */}
            <div className="relative" dir="ltr">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className={`${FIELD} pe-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
                title={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
                className="absolute end-2.5 top-1/2 -translate-y-1/2 text-subtle hover:text-text transition-colors"
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </label>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-[var(--color-primary)]"
            />
            {t('auth.remember.label')}
          </label>

          <button type="submit" disabled={busy || !configured} className={remembered && passkeysSupported ? SECONDARY : BUTTON}>
            {busy ? t('auth.signingIn') : t('auth.signIn')}
          </button>

          <button
            type="button"
            onClick={() => setForgot(true)}
            className="w-full text-center text-sm text-muted hover:text-primary transition-colors"
          >
            {t('auth.forgot')}
          </button>

          {passkeysSupported && !remembered ? (
            <>
              <div className="flex items-center gap-3 pt-1" aria-hidden="true">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[11px] uppercase tracking-wider text-subtle">
                  {t('auth.passkey.or')}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
              {/*
                Cognito finds a passkey by the account it belongs to, so the email comes
                first. The button still works when it is empty — it says what is missing
                instead of being disabled with no explanation.
              */}
              <button
                type="button"
                disabled={busy || !configured}
                onClick={() => {
                  if (!email.trim()) {
                    setError(t('auth.passkey.needEmail'));
                    return;
                  }
                  passkeySignIn(email.trim());
                }}
                className={SECONDARY}
              >
                <PasskeyIcon />
                {t('auth.passkey.signIn')}
              </button>
            </>
          ) : null}
        </form>
      ) : null}

      {step.kind === 'secure' ? (
        <div className="mt-5 space-y-2.5">
          {passkeysSupported ? (
            <ChoiceButton
              onClick={() => void run(addPasskeyAndContinue)}
              disabled={busy}
              icon={<PasskeyIcon />}
              title={t('auth.secure.passkey')}
              hint={t('auth.secure.passkeyHint')}
              badge={t('auth.secure.recommended')}
            />
          ) : null}
          <ChoiceButton
            onClick={() => void run(chooseAuthenticatorApp)}
            disabled={busy}
            icon={<PhoneIcon />}
            title={t('auth.secure.app')}
            hint={t('auth.secure.appHint')}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(skipSecondFactor)}
            className="w-full pt-1.5 text-center text-sm text-muted hover:text-primary transition-colors"
          >
            {t('auth.secure.skip')}
          </button>
        </div>
      ) : null}

      {step.kind === 'mfa' ? (
        <form onSubmit={onChallenge} className="mt-5 space-y-3.5">
          <CodeInput label={t('auth.mfa.code')} value={code} onChange={setCode} />
          <button type="submit" disabled={busy || code.trim().length < 6} className={BUTTON}>
            {busy ? t('auth.signingIn') : t('auth.mfa.verify')}
          </button>
          <BackLink onClick={resetStep} label={t('auth.back')} />
        </form>
      ) : null}

      {step.kind === 'setupMfa' ? (
        <form onSubmit={onChallenge} className="mt-5 space-y-3.5">
          {/*
            The QR is rendered from the otpauth:// URI by a tiny local encoder rather
            than an image service — the secret must not leave the browser, and a
            third-party chart URL would put it in someone else's access log.
          */}
          <div className="flex justify-center">
            <QrCode text={step.uri} />
          </div>

          <div className="text-center">
            <p className="text-xs text-muted">{t('auth.setupMfa.manual')}</p>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(step.secret).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                });
              }}
              className="mt-1 font-mono text-xs tracking-wider break-all text-text hover:text-primary transition-colors"
            >
              {step.secret}
            </button>
            {copied ? (
              <p className="mt-1 text-xs text-primary" role="status">
                {t('auth.setupMfa.copied')}
              </p>
            ) : null}
          </div>

          <CodeInput label={t('auth.mfa.code')} value={code} onChange={setCode} />
          <button type="submit" disabled={busy || code.trim().length < 6} className={BUTTON}>
            {busy ? t('auth.signingIn') : t('auth.setupMfa.confirm')}
          </button>
          {/* Portal-offered enrolment can always be put off; see offerSecondFactor. */}
          {step.enrol === 'portal' ? (
            <BackLink onClick={() => void run(skipSecondFactor)} label={t('auth.setupMfa.skip')} />
          ) : (
            <BackLink onClick={resetStep} label={t('auth.back')} />
          )}
        </form>
      ) : null}

      {step.kind === 'newPassword' ? (
        <form onSubmit={onNewPassword} className="mt-5 space-y-3.5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">
              {t('auth.newPassword.password')}
            </span>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              required
              minLength={12}
              className={FIELD}
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold text-muted">
              {t('auth.newPassword.confirm')}
            </span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={12}
              className={FIELD}
            />
          </label>

          <p className="text-xs text-subtle">{t('auth.newPassword.rules')}</p>

          <button type="submit" disabled={busy} className={BUTTON}>
            {busy ? t('auth.signingIn') : t('auth.newPassword.submit')}
          </button>
          <BackLink onClick={resetStep} label={t('auth.back')} />
        </form>
      ) : null}

    </Shell>
  );
}

const SECONDARY =
  'w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg border border-border ' +
  'bg-surface text-sm font-semibold text-text transition-colors hover:bg-surface-alt ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

function PasskeyIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="6.5" r="3" />
      <path d="M2 16.5c0-2.8 2.2-5 5-5 1.2 0 2.3.4 3.1 1.1" />
      <circle cx="14.5" cy="11.5" r="2.2" />
      <path d="M14.5 13.7v4M14.5 16h1.6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <rect x="5.5" y="2" width="9" height="16" rx="2" />
      <path d="M8.5 15h3" />
    </svg>
  );
}

/** One option on the "secure your account" step: icon, title, one line of why. */
function ChoiceButton({
  onClick,
  disabled,
  icon,
  title,
  hint,
  badge,
}: {
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  title: string;
  hint: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-start gap-3 rounded-lg border border-border bg-surface p-3.5 text-start transition-colors hover:border-[var(--color-primary)] hover:bg-surface-alt disabled:opacity-60"
    >
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-soft)] text-primary">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-semibold">
          {title}
          {badge ? (
            <span className="rounded-full bg-[var(--color-primary-soft)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
              {badge}
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-muted">{hint}</span>
      </span>
    </button>
  );
}

function BackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-center text-sm text-muted hover:text-primary transition-colors"
    >
      {label}
    </button>
  );
}

/**
 * A six-digit box.
 *
 * inputMode numeric brings up the number pad on a phone, and one-time-code lets both
 * iOS and Android offer the code straight from the notification.
 */
function CodeInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-muted">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        required
        className={`${FIELD} text-center text-lg font-mono tracking-[0.4em]`}
        placeholder="000000"
      />
    </label>
  );
}

/** The page frame: one card holding the mark, the name and the form. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg text-text flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
          {/* Inside the card with the form, not floating above it: one object on the
              page rather than two, which is what stops the eye before the fields. */}
          <div className="mb-5 flex flex-col items-center gap-2">
            <Logo size={46} className="text-primary" />
            <div className="text-base font-bold leading-none">HH Electrical Systems</div>
          </div>

          {children}
        </div>

        <div className="mt-6 flex justify-center">
          {/* Language and theme only. Someone who cannot read the form needs to reach
              these before signing in; settings lives behind the gate. */}
          <PreferenceToggles compact showSettings={false} />
        </div>
      </div>
    </div>
  );
}

/**
 * A QR code, drawn here rather than fetched.
 *
 * The payload is a TOTP secret. Sending it to an external QR service would hand that
 * secret to a third party and put it in a URL, which is exactly where secrets should
 * never be. `qrcode` is already a dependency for the inventory labels.
 */
function QrCode({ text }: { text: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('qrcode').then(async (qr) => {
      const url = await qr.toDataURL(text, { margin: 1, width: 180 });
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [text]);

  if (!src) return <div className="h-[180px] w-[180px] rounded-lg bg-surface-alt" />;
  return (
    <img
      src={src}
      alt=""
      width={180}
      height={180}
      // White plate: a QR code on a dark background does not scan.
      className="rounded-lg bg-white p-2"
    />
  );
}
